import { useEffect, useState } from 'react';
import { createVoiceActivityPublisher } from './useLiveVoiceActivity';

const EMPTY = [];
function publicGuests(payload) {
  if (!Array.isArray(payload?.guests)) return null;
  const seen = new Set();
  return payload.guests.filter(guest => {
    if (!guest || typeof guest.id !== 'string' || !guest.id || seen.has(guest.id)) return false;
    seen.add(guest.id);
    return true;
  }).map(guest => ({
    id: guest.id, name: typeof guest.name === 'string' ? guest.name.slice(0, 32) || 'Guest' : 'Guest',
    role: guest.role === 'streamer' ? 'streamer' : 'viewer', inVoice: guest.inVoice === true,
    speaking: guest.inVoice === true && guest.speaking === true,
    activityKnown: guest.inVoice === true && guest.activityKnown === true,
  }));
}

// voiceMembers/voiceStreams are accepted for the agreed page API, but the
// authoritative roster is the ONLY membership source. No remote audio analysis:
// nonvoice observers receive the same speaking state without making RTC peers.
export function useLiveGuestPresence({ socket, localStream = null, localMuted = false }) {
  const [presence, setPresence] = useState({ socket: null, guests: EMPTY, connected: false });
  useEffect(() => {
    if (!socket) return undefined;
    let active = true;
    const clear = connected => { if (active) setPresence({ socket, guests: EMPTY, connected }); };
    const connect = () => {
      clear(true);
      socket.emit('get_presence'); // No room joins, emulator reloads or RTC setup.
    };
    const disconnect = () => clear(false);
    const snapshot = payload => {
      if (!active || socket.connected === false) return;
      const guests = publicGuests(payload);
      if (guests) setPresence({ socket, guests, connected: true });
    };
    socket.on('connect', connect);
    socket.on('disconnect', disconnect);
    socket.on('live_presence', snapshot);
    if (socket.connected) connect(); // Recover a snapshot missed before mounting.
    else clear(false);
    return () => {
      active = false;
      socket.off?.('connect', connect);
      socket.off?.('disconnect', disconnect);
      socket.off?.('live_presence', snapshot);
    };
  }, [socket]);
  const guests = presence.socket === socket && presence.connected ? presence.guests : EMPTY;
  const localId = socket?.id;
  const inVoice = guests.some(guest => guest.id === localId && guest.inVoice);
  useEffect(() => {
    if (!socket || !presence.connected || presence.socket !== socket || !inVoice) return undefined;
    const publish = speaking => {
      if (socket.connected !== false) socket.emit('voice_activity', { speaking });
    };
    const stop = createVoiceActivityPublisher({ stream: localStream, muted: localMuted, publish });
    return () => { stop(); publish(null); };
  }, [socket, presence.socket, presence.connected, inVoice, localStream, localMuted]);
  // Mute/off is immediate locally while the server's reliable clear propagates.
  return guests.map(guest => guest.id === localId && (localMuted || !localStream)
    ? { ...guest, speaking: false } : guest);
}
